import { filter, find } from 'lodash'
import { create } from 'zustand'
import { createJSONStorage, persist, subscribeWithSelector } from 'zustand/middleware'
import { immer } from 'zustand/middleware/immer'

import { Color, Finishing } from '@/types'

type State = {
  wishlist: Color[]
}

type Actions = {
  // eslint-disable-next-line no-unused-vars
  addToWishlist: (item: Color) => void
  // eslint-disable-next-line no-unused-vars
  removeFromWishlist: (id: number) => void
  // eslint-disable-next-line no-unused-vars
  addFinishingToColor: (id: number, finishing: Finishing) => void
  // eslint-disable-next-line no-unused-vars
  removeFinishingFromColor: (id: number) => void
  // eslint-disable-next-line no-unused-vars
  enableFromWishlist: (id: number) => void
  // eslint-disable-next-line no-unused-vars
  disableFromWishlist: (id: number) => void
  // eslint-disable-next-line no-unused-vars
  toggleFromWishlist: (color: Color, wishlist: Color[]) => void
  // eslint-disable-next-line no-unused-vars
  loadWishlistFromFile: (file: File) => Promise<void>
  clearWishlist: () => void
}

const initialState: State = {
  wishlist: []
}

const useWishlistStore = create(
  persist(
    subscribeWithSelector(
      immer<State & Actions>((set) => ({
        ...initialState,

        addToWishlist: (color) =>
          set((state) => {
            state.wishlist.push(color)
            console.log('Adding to wishlist:', color)
          }),

        removeFromWishlist: (id) =>
          set((state) => {
            console.log('Removing from wishlist:', id)
            state.wishlist = state.wishlist.filter((color) => color.id !== id)
          }),

        addFinishingToColor: (id, finishing) =>
          set((state) => {
            console.log('Adding finishing to color:', { id, finishing })
            state.wishlist.forEach((color) => {
              if (color.id === id) {
                color.finishing = finishing
              }
            })
          }),

        removeFinishingFromColor: (id) =>
          set((state) => {
            console.log('Removing finishing from color:', id)
            state.wishlist.forEach((color) => {
              if (color.id === id) {
                color.finishing = null
              }
            })
          }),

        enableFromWishlist: (id) =>
          set((state) => {
            console.log('Enabling from wishlist:', id)
            state.wishlist.forEach((color) => {
              if (color.id === id) {
                color.enabled = true
              }
            })
          }),

        disableFromWishlist: (id) =>
          set((state) => {
            console.log('Disabling from wishlist:', id)
            state.wishlist.forEach((color) => {
              if (color.id === id) {
                color.enabled = false
              }
            })
          }),

        toggleFromWishlist: (color, wishlist) =>
          set((state) => {
            console.log('Toggling from wishlist:', color.id)
            isInWishlist(color.id, wishlist, true)
              ? isInWishlist(color.id, wishlist)
                ? hasFinishing(color.id, wishlist)
                  ? state.disableFromWishlist(color.id)
                  : state.removeFromWishlist(color.id)
                : state.enableFromWishlist(color.id)
              : state.addToWishlist({ ...color, finishing: null, enabled: true })
          }),

        loadWishlistFromFile: (file: File) =>
          new Promise((resolve, reject) => {
            console.log('Loading wishlist from file:', file)
            const reader = new FileReader()
            reader.onload = (e) => {
              try {
                const content = e.target?.result as string
                const wishlist = JSON.parse(content)
                console.group(wishlist)
                set((state) => {
                  state.wishlist = [...wishlist]
                })
                resolve()
              } catch (error) {
                console.error('Error parsing wishlist file:', error)
                reject(error)
              }
            }
            reader.onerror = () => reject(reader.error || new Error('Unable to read wishlist file'))
            reader.readAsText(file)
          }),

        clearWishlist: () =>
          set((state) => {
            console.log('Emptying wishlist')
            state.wishlist = []
          })
      }))
    ),
    {
      name: 'coloridecora-wishlist', // name of the item in the storage (must be unique)
      storage: createJSONStorage(() => localStorage) // (optional) by default, 'localStorage' is used
    }
  )
)

const selectEnabledWishlist = (wishlist: Color[]) => filter(wishlist, { enabled: true })

const isInWishlist = (id: number, wishlist: Color[], showAll = false) =>
  find(showAll ? wishlist : selectEnabledWishlist(wishlist), { id }) !== undefined

const hasFinishing = (id: number, wishlist: Color[]) => find(wishlist, { id })?.finishing !== null

export { hasFinishing, isInWishlist, selectEnabledWishlist, useWishlistStore }
